import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ListItem } from '../../../models/api/core/list/list-item';
import { ReportPreference } from '../../../models/api/reports/report-helpers/report-preference';
import { ReportsService } from '../../../services/reports/reports.service';
import { Snackbar } from '../../../services/snackbar/snackbar.service';
import { BaseComponent } from '../../base/base.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { RadioButtonComponent } from '../../core/controls/radio-button.component';
import { SelectComponent } from '../../core/controls/select.component';
import { HelpIconComponent } from '../../core/labels/help-icon.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { DialogComponent } from '../../core/panels/dialog.component';
import { LoadingPanelComponent } from '../../core/panels/loading-panel.component';
import { HandleLoadingState } from '../../../decorators/handle-loading-state.decorator';

interface PreferenceFormValues {
  outputType?: ListItem | null;
  reportName?: ListItem | null;
  includeOnReport?: ListItem[];
  autoFitColumns?: string | null;
}

@Component({
  selector: 'app-report-pref-dialog',
  imports: [
    CommonModule,
    ActionRowComponent,
    ButtonComponent,
    ReactiveFormsModule,
    DialogComponent,
    SelectComponent,
    RadioButtonComponent,
    HelpIconComponent,
    LoadingPanelComponent,
  ],
  template: `
    <app-dialog #dialog label="Report preferences" width="50rem">
      <ng-template #content>
        <app-loading-panel
          [state]="loading ? 'Loading' : 'Loaded'"
          loadingStyle="Box"
          [loadingHeight]="400"
        >
          <ng-template #loaded>
            <div *ngTemplateOutlet="body"></div>
          </ng-template>
        </app-loading-panel>
      </ng-template>
    </app-dialog>
    <ng-template #body>
      <form class="" [formGroup]="fg">
        <div class="tw-col-span-2 tw-flex tw-pb-4">
          <div class="tw-grow tw-flex tw-gap-2 tw-pt-2">
            <span id="report-pref-format-label" class="label tw-font-bold">
              Report format
            </span>
            <app-help-icon
              [helpText]="outputHelpText"
              ariaLabel="Report format information"
              size="20px"
            />
          </div>
          <app-select
            [options]="fileTypeItems"
            class="tw-pt-1 tw-min-w-[220px] tw-max-w-[300px]"
            optionLabel="description"
            [optionValue]="null"
            [control]="fg.controls.outputType"
            [showClear]="false"
            [virtualScroll]="false"
            [filter]="false"
            label=""
            ariaLabelledBy="report-pref-format-label"
            screenReaderLabel="Report format"
            [showHelpText]="false"
          />
        </div>
        <div class="tw-col-span-2 tw-flex tw-pb-4">
          <span
            id="report-pref-report-name-label"
            class="label tw-grow tw-font-bold tw-pt-2"
          >
            Report name to include
          </span>
          <app-select
            [options]="reportNameItems"
            class="tw-min-w-[220px] tw-max-w-[300px]"
            optionLabel="description"
            [optionValue]="null"
            [control]="fg.controls.reportName"
            [showClear]="false"
            [virtualScroll]="false"
            [filter]="false"
            label=""
            ariaLabelledBy="report-pref-report-name-label"
            screenReaderLabel="Report name to include"
            [showHelpText]="false"
          />
        </div>
        <div class="tw-col-span-2 tw-flex tw-pb-4">
          <span
            id="report-pref-include-on-report-label"
            class="label tw-grow tw-font-bold tw-pt-2"
          >
            Fields to include on report header
          </span>
          <app-select
            [options]="includeReportItems"
            class="tw-w-[320px]"
            optionLabel="description"
            [optionValue]="null"
            [control]="fg.controls.includeOnReport"
            [virtualScroll]="false"
            [filter]="false"
            label=""
            ariaLabelledBy="report-pref-include-on-report-label"
            screenReaderLabel="Fields to include on report header"
            [showHelpText]="false"
            [showClear]="false"
            [multiple]="true"
            [multipleFilter]="true"
            [chip]="true"
            [showHeader]="false"
          />
        </div>
        <div class="tw-gap-16 tw-flex tw-pb-4">
          <div class="tw-grow tw-flex tw-gap-2 tw-pt-2">
            <span class="label tw-font-bold">Auto-fit column width</span
            ><app-help-icon
              [helpText]="columnWidthHelpText"
              ariaLabel="Auto-fit column width information"
              size="20px"
            />
          </div>
          <app-radio-button
            [options]="radioOptions"
            optionValue="value"
            optionLabel="description"
            [control]="fg.controls.autoFitColumns"
            screenReaderLabel="Auto-fit column width"
            [announceGroupLabelWithOptions]="true"
            [vertical]="false"
            [showHelpText]="false"
          />
        </div>
      </form>
      <app-action-row classes="tw-pt-4">
        <app-button
          left1
          label="Restore default"
          emphasis="Low"
          (click)="restoreDefaults()"
        />
        <app-button
          right1
          label="Save"
          [disabled]="loading || !hasChanges"
          (click)="savePref()"
        />
        <app-button right2 label="Cancel" emphasis="Low" (click)="close()" />
      </app-action-row>
    </ng-template>
  `,
  styles: ``,
})
export class ReportPrefDialogComponent extends BaseComponent implements OnInit {
  reports = inject(ReportsService);
  private snackbar = inject(Snackbar);

  @ViewChild('dialog')
  dialog: DialogComponent;

  outputHelpText = 'Applies when the report supports the defaulted format.';
  columnWidthHelpText = 'Applies when exporting to Excel.';

  fileTypeItems: ListItem[] = [
    { value: 'PDF', description: 'PDF' },
    { value: 'XLSX', description: 'XLSX' },
    { value: 'XLS', description: 'XLS' },
    { value: 'CSV', description: 'CSV' },
  ];

  reportNameItems: ListItem[] = [
    { value: 'S', description: 'System defined report name' },
    { value: 'U', description: 'User defined report name' },
    { value: 'E', description: 'Exclude' },
  ];

  includeReportItems: ListItem[] = [
    { value: 'clientName', description: 'Client name' },
    { value: 'criteria', description: 'Criteria' },
    { value: 'asOfDate', description: 'As of date' },
    { value: 'scheduleDetails', description: 'Schedule details' },
    { value: 'currencyFormat', description: 'Currency format' },
  ];

  radioOptions = [
    { value: 'Y', description: 'Yes' },
    { value: 'N', description: 'No' },
  ];

  fg = new FormGroup({
    outputType: new FormControl<ListItem>(null),
    reportName: new FormControl<ListItem>(null),
    includeOnReport: new FormControl<ListItem[]>([]),
    autoFitColumns: new FormControl<string>(null),
  });

  hasChanges = false;
  loading = false;
  initialValues: PreferenceFormValues | null = null;
  preferencesChanged = false;

  readonly defaultValues: PreferenceFormValues = {
    outputType: { value: 'PDF', description: 'PDF' },
    reportName: { value: 'S', description: 'System defined report name' },
    includeOnReport: [
      { value: 'clientName', description: 'Client name' },
      { value: 'asOfDate', description: 'As of date' },
    ],
    autoFitColumns: 'N',
  };

  ngOnInit() {
    this.dispose.subs = this.fg.valueChanges.subscribe(() => {
      this.hasChanges = this.checkForChanges();
    });
  }

  checkForChanges(): boolean {
    if (!this.initialValues) return false;

    const current = this.fg.value;
    return (
      current.outputType?.value !== this.initialValues.outputType?.value ||
      current.reportName?.value !== this.initialValues.reportName?.value ||
      current.autoFitColumns !== this.initialValues.autoFitColumns ||
      JSON.stringify(current.includeOnReport) !==
        JSON.stringify(this.initialValues.includeOnReport)
    );
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  async show() {
    this.dialog.visible = true;
    await this.loadPreferences(this.preferencesChanged);
    this.preferencesChanged = false;
  }

  @HandleLoadingState()
  async loadPreferences(refreshCache = false) {
    const prefs =
      await this.api.reportHelpers.getReportPreferences(refreshCache);

    if (prefs) {
      const outputType = this.fileTypeItems.find(
        (item) => item.value === prefs.outputType,
      );
      this.fg.controls.outputType.setValue(outputType);

      const reportName = this.reportNameItems.find(
        (item) => item.value === prefs.reportName,
      );
      this.fg.controls.reportName.setValue(reportName);

      const includeItems =
        prefs.includeOnReport
          ?.map((value: string) =>
            this.includeReportItems.find((item) => item.value === value),
          )
          .filter((item: ListItem | undefined) => item !== undefined) || [];
      this.fg.controls.includeOnReport.setValue(includeItems);

      this.fg.controls.autoFitColumns.setValue(prefs.autoFitColumns);
    }

    this.initialValues = { ...this.fg.value };
    this.hasChanges = false;
  }

  close() {
    this.dialog.visible = false;
  }

  restoreDefaults() {
    this.fg.patchValue(this.defaultValues);
    this.hasChanges = this.checkForChanges();
  }

  @HandleLoadingState()
  async savePref() {
    const includeItems = this.fg.controls.includeOnReport.value || [];

    const pref: ReportPreference = {
      outputType: this.fg.controls.outputType.value?.value || 'PDF',
      reportName: this.fg.controls.reportName.value?.value || 'S',
      autoFitColumns: this.fg.controls.autoFitColumns.value || 'N',
      accountSearch: 'N',
      includeOnReport: includeItems.map((item) => item.value),
    };

    await this.api.reportHelpers.saveReportPreferences(pref);
    this.snackbar.show('Report preferences saved successfully', 'Success');

    // Set flag to refresh cache on next load
    this.preferencesChanged = true;

    // Update initial values after save
    this.initialValues = { ...this.fg.value };
    this.hasChanges = false;

    this.close();
  }
}
